'use client';

import { useQuery } from '@tanstack/react-query';

import { FindBookingForSlotParams, findBookingForSlot } from '@website/services/bookings.service';

export interface UseBookingRecoveryParams {
  activityId: string | undefined;
  slotId: string | null;
  bookingDate: string | null;
  startTime: number | null;
  endTime: number | null;
  /** Skip the query if we already have a bookingId */
  skip?: boolean;
}

/**
 * Hook to recover a booking when the user reloads the payment page.
 * Uses React Query for caching and deduplication of API calls.
 * Returns ANY booking for the slot (regardless of payment status).
 * The caller should check the paymentStatus and handle accordingly.
 */
export function useBookingRecovery({
  activityId,
  slotId,
  bookingDate,
  startTime,
  endTime,
  skip = false
}: UseBookingRecoveryParams) {
  // Build query params - only enable query if all required params are present
  const hasAllParams = !!activityId && !!slotId && !!bookingDate && startTime !== null && endTime !== null && !skip;

  const queryParams: FindBookingForSlotParams | null = hasAllParams
    ? {
        activityId: activityId!,
        slotId: slotId!,
        bookingDate: bookingDate!,
        startTime: startTime!,
        endTime: endTime!
      }
    : null;

  return useQuery({
    queryKey: ['bookingRecovery', activityId, slotId, bookingDate, startTime, endTime],
    queryFn: () => findBookingForSlot(queryParams!),
    enabled: hasAllParams,
    staleTime: 60 * 1000, // Cache for 1 minute
    retry: 1, // Only retry once on failure
    refetchOnWindowFocus: false // Don't refetch when window regains focus
  });
}

// Keep old name as alias for backwards compatibility
export const usePendingBookingRecovery = useBookingRecovery;
export type UsePendingBookingRecoveryParams = UseBookingRecoveryParams;
